/* eslint-disable react/jsx-props-no-spreading */
import React, { SyntheticEvent, useCallback, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { FileRejection, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import Button from '../../../components/form/button/Button';
import Icon from '../../../components/icon/Icon';
import InteractiveElement from '../../../components/interactive-element/InteractiveElement';
import NoResultsPage from '../../../components/no-results-page/NoResultsPage';
import Table from '../../../components/table/Table';
import TBody from '../../../components/table/TBody/TBody';
import TD from '../../../components/table/TD/TD';
import TH from '../../../components/table/TH/TH';
import THead from '../../../components/table/THead/THead';
import TR from '../../../components/table/TR/TR';
import { BASE_URL, FILE_LOCATION } from '../../../core/constants/api';
import { SIZE_50MB } from '../../../core/constants/file';
import { GlobalNotificationTypes } from '../../../core/enums/GlobalNotificationTypes';
import ProjectDocumentsService from '../../../core/services/ProjectDocumentsService';
import { spawnGlobalNotification } from '../../../core/state/global-notifications/asyncActions';
import { NotificationPosition } from '../../../core/state/global-notifications/types';
import { getProjectDocuments, getProjectFile } from '../../../core/state/project/asyncActions';
import { RootState } from '../../../core/state/state';
import ConfirmModal from '../../components/confirm-modal/ConfirmModal';
import TableWrapper from '../../components/table-wrapper/TableWrapper';
import Image from '../../../components/image/Image';
import Role from '../../components/role/Role';
import { UserRoleType } from '../../../core/state/user/types';
import defaultUserImage from '../../../../img/user-default.png';
import ActionDropdown from '../../../components/action-dropdown/ActionDropdown';
import { useInternalUser } from '../../../core/hooks/useInternalUser';

import './Documents.scss';

const ACCEPTED_FILES = '.pdf';

const Documents: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const params = useParams<{ projectId: string }>();
  const projectId = Number(params.projectId);
  const documents = useSelector((state: RootState) => state.projects.projectDocuments);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedFileId, setSelectedFileId] = useState<number>(0);
  const isInternal = useInternalUser();

  const onDropRejected = useCallback(
    (rejections: FileRejection[]) => {
      const errorCode = rejections[0].errors[0].code;
      let message;
      let title;
      switch (errorCode) {
        case 'file-too-large':
          title = t('file.sizeValidation.title');
          message = t('file.sizeValidation.text');
          break;
        case 'file-invalid-type':
          title = t('file.typeValidation.title');
          message = t('file.typeValidation.text');
          break;

        default:
          message = t('file.unknownError.default');
      }

      dispatch(
        spawnGlobalNotification({
          type: GlobalNotificationTypes.Error,
          title,
          message,
          delay: 5000
        })
      );
    },
    [dispatch, t]
  );

  const onDropAccepted = React.useCallback(
    async (acceptedFiles) => {
      const file = acceptedFiles[0];

      const formData = new FormData();
      formData.append('files', file, file.name);

      await ProjectDocumentsService.uploadProjectDocument(Number(projectId), null, formData);
      dispatch(getProjectDocuments(projectId));
      dispatch(
        spawnGlobalNotification({
          postition: NotificationPosition.Top,
          type: GlobalNotificationTypes.Success,
          title: t('common.notification.file.title'),
          message: `${t('projectSettings.documents.uploadSuccess')} ${file.name}`
        })
      );
    },
    [dispatch, projectId, t]
  );
  const { getRootProps, getInputProps, open } = useDropzone({
    onDropAccepted,
    multiple: false,
    accept: ACCEPTED_FILES,
    maxSize: SIZE_50MB,
    onDropRejected
  });

  React.useEffect(() => {
    dispatch(getProjectDocuments(projectId));
  }, [dispatch, projectId]);

  return (
    <TableWrapper>
      <div className="table__header">
        <div className="documents__header">
          <span>{t('projectSettings.documents.title')}</span>
          {isInternal && <span className="documents__subtitle">{t('projectSettings.documents.subtitle')}</span>}
        </div>
        <div>
          <Role inRoles={[UserRoleType.ProjectAdmin, UserRoleType.SystemAdmin]}>
            <div {...getRootProps({ className: 'dropzone' })}>
              <input {...getInputProps()} />
              <Button buttonTypes={['blue', 'small']} onClick={() => open}>
                <Icon className="documents__icon-plus" icon="plus" size="extra-small" svg />
                <span>{t('common.uploadNew')}</span>
              </Button>
            </div>
          </Role>
        </div>
      </div>
      <div className="documents__table">
        <Table>
          <THead>
            <TR>
              <TH>{t('projectSettings.documents.heading.fileName')}</TH>
              <TH>{t('projectSettings.documents.heading.uploadDate')}</TH>
              <TH>{t('projectSettings.documents.heading.uploadTime')}</TH>
              <TH>{t('projectSettings.documents.heading.uploadedBy')}</TH>
              <TH />
            </TR>
          </THead>
          <TBody>
            {documents?.map((document) => (
              <TR className="documents__item" key={document.id}>
                <TD className="d-flex align-items-center">
                  <div className="documents__item-img">
                    <Icon className="documents__item-icon" icon="file" svg size="small" />
                  </div>
                  <InteractiveElement className="documents__item-name" onClick={() => window.open(`${FILE_LOCATION}/${document.fullName}`, '_file')}>
                    <span className="font-weight-bold">{document.name}</span>
                  </InteractiveElement>
                </TD>
                <TD className="text-left">{document.uploadDate}</TD>
                <TD className="text-left">
                  {new Date(`${document.uploadDate.split('.').reverse().join('-')}T${document.uploadTime}.000Z`).toLocaleTimeString()}
                </TD>
                <TD className="text-left">
                  <Image
                    className="internal-users__user-img"
                    src={`${BASE_URL}/${document.profilePhoto}`}
                    alt="user avatar"
                    onError={(e: SyntheticEvent<HTMLImageElement, Event>) => {
                      e.currentTarget.src = defaultUserImage;
                    }}
                  />
                  <span className="font-weight-bold">{document.createdBy}</span>
                </TD>
                <TD>
                  <ActionDropdown>
                    <Dropdown.Item
                      onClick={async () => {
                        await dispatch(getProjectFile(projectId, document.id));
                        dispatch(
                          spawnGlobalNotification({
                            postition: NotificationPosition.Top,
                            type: GlobalNotificationTypes.Success,
                            title: t('projectSettings.documents.notification.fileDownloaded.title'),
                            message: `${t('projectSettings.documents.notification.fileDownloaded.text')} ${document.name}`
                          })
                        );
                      }}
                    >
                      {t('common.downloadFile')}
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => window.open(`${FILE_LOCATION}/${document.fullName}`, '_file')}>
                      {t('common.openFile')}
                    </Dropdown.Item>
                    <Role inRoles={[UserRoleType.ProjectAdmin, UserRoleType.SystemAdmin]}>
                      {!document.isProjectPlan && (
                        <Dropdown.Item
                          onClick={() => {
                            setSelectedFileId(document.id);
                            setIsModalOpen(true);
                          }}
                        >
                          {t('common.modal.file.remove.title')}
                        </Dropdown.Item>
                      )}
                    </Role>
                  </ActionDropdown>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
        {!documents ||
          (documents.length === 0 && (
            <NoResultsPage
              title={t('projectSettings.documents.noData.title')}
              description={t('projectSettings.documents.noData.text')}
              icon="files"
              iconClass="documents__no-result-icon"
            />
          ))}
      </div>
      <ConfirmModal
        height={196}
        titleText={t('projectSettings.documents.modal.removeFile.title')}
        contentText={t('projectSettings.documents.modal.removeFile.text')}
        isOpen={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onConfirm={async () => {
          await ProjectDocumentsService.deleteProjectDocument(projectId, selectedFileId);
          dispatch(getProjectDocuments(Number(projectId)));
          setSelectedFileId(0);
          setIsModalOpen(false);
          dispatch(
            spawnGlobalNotification({
              type: GlobalNotificationTypes.Success,
              postition: NotificationPosition.Top,
              title: t('projectSettings.documents.notification.fileRemoved.title'),
              message: t('projectSettings.documents.notification.fileRemoved.text')
            })
          );
        }}
      />
    </TableWrapper>
  );
};

export default Documents;
